import { z } from "zod";
import { TFile, TObjectBase } from "../base";
import { TTaskSettings } from "../task/taskSettings";
import { TDeviationVisibility, TFirebaseTimestamp } from "../deprecated";
import { TRelatives } from "../common/relative";
import { TTaskInvoicedTo } from "../task/taskInvoice";

export const TRoutineRepeatType = z.enum(["1", "2", "3", "4", "last"]);
export type IRoutineRepeatType = z.infer<typeof TRoutineRepeatType>;

export const TRoutineFrequency = z.object({
  each: z.number(),
  running: z.object({}).optional(),
  daily: z.object({}).optional(),
  weekly: z
    .object({
      days: z.array(z.number()),
    })
    .optional(),
  monthly: z
    .object({
      repeatType: TRoutineRepeatType.optional(),
      repeatWeekday: z.number().optional(),
      repeatDate: z.number().optional(),
    })
    .optional(),
  yearly: z
    .object({
      months: z.array(z.number()),
      days: z.array(z.number()).optional(),
      repeatType: TRoutineRepeatType.optional(),
      repeatWeekday: z.number().optional(),
      repeatDate: z.number().optional(),
      repeatEveryWeeks: z.number().optional(),
    })
    .optional(),
});
export type IRoutineFrequency = z.infer<typeof TRoutineFrequency>;

export const TRoutineTask = z.object({
  title: z.string(),
  description: z.string(),
  fields: z
    .record(
      z.object({
        title: z.string(),
        value: z.any().optional(),
        valueType: z.string().optional(),
        unit: z.string().optional(),
        options: z.array(z.string()).optional(),
      })
    )
    .optional(),
  fieldsOrder: z.array(z.string()).optional(),
  responsible: z.record(z.object({})).optional(),
  relatives: TRelatives.optional(),
});

export const TRawRoutine = z
  .object({
    title: z.string(),
    companies: z.array(z.string()).optional(),
    propertyIDs: z
      .record(
        z.object({
          units: z.array(z.string()).optional(),
          collections: z.record(z.array(z.string())).optional(),
          responsibles: z.array(z.string()).optional(),
          responsiblesPerUnit: z.record(z.array(z.string())).optional(),
          executors: z.array(z.string()).optional(),
          executorsPerUnit: z.record(z.array(z.string())).optional(),
        })
      )
      .optional(),
    active: z.boolean(),
    generateUniqueTaskPerUnit: z.boolean().optional(),
    startDate: TFirebaseTimestamp,
    frequency: TRoutineFrequency,
    visibility: TDeviationVisibility,
    description: z.string().optional(),
    createDaysBefore: z.number().optional(),
    deadlineDelay: z.number().optional(),
    tags: z.array(z.string()).optional(),
    priority: z.number().optional(),
    placeDescription: z.string().optional(),
    floors: z.array(z.number()).optional(),
    contractorGroups: z.array(z.string()).optional(),
    files: z.record(TFile).optional(),
    tasks: z.record(TRoutineTask).optional(),
    taskOrder: z.array(z.string()).optional(),
    taskListNumbered: z.boolean().optional(),
    responsibles: z.array(z.string()).optional(),
    responsiblesType: z
      .enum(["all", "property", "unit", "fireSafetyResponsibles"])
      .optional(),
    executors: z.array(z.string()).optional(),
    executorsType: z
      .enum(["all", "property", "unit", "fireSafetyResponsibles"])
      .optional(),
    settings: TTaskSettings.optional(),
    collectionItemIDs: z.array(z.string()).optional(),
    taskTypeID: z.string().optional(),
    invoicedTo: TTaskInvoicedTo.nullish(),
  })
  .extend(TObjectBase.shape);
export type IRawRoutine = z.infer<typeof TRawRoutine>;

export const TRawRoutineActivity = z.object({
  id: z.string(),
  dateStr: z.string(),
  propertyID: z.string(),
  unitID: z.string().optional(),
  deviationID: z.string().nullish(),
  status: z.number(),
  deleted: z.boolean().optional(),
});
export type IRawRoutineActivity = z.infer<typeof TRawRoutineActivity>;

export const TRawDeviationTask = z
  .object({
    title: z.string(),
    creatorID: z.string().optional(),
    createDate: TFirebaseTimestamp.optional(),
    description: z.string().optional(),
    status: z
      .object({
        value: z.number().optional(),
        changedBy: z.string().optional(),
        changedDate: TFirebaseTimestamp.optional(),
      })
      .optional(),
    routineTaskID: z.string().optional(),
    fields: z
      .record(
        z.object({
          title: z.string().optional(),
          value: z.any().optional(),
          valueType: z.string().optional(),
          unit: z.string().optional(),
          options: z.array(z.string()).optional(),
        })
      )
      .optional(),
    fieldsOrder: z.array(z.string()).optional(),
    relatives: TRelatives.optional(),
    files: z.record(TFile).optional(),
    responsible: z.record(z.object({})).optional(),
  })
  .extend(TObjectBase.shape);
export type IRawDeviationTask = z.infer<typeof TRawDeviationTask>;
